/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Channel as IChannel,
    SourceType,
    StationPolicy,
    VideoResolution,
} from 'client-code/channels-service';

import { CachingPolicy } from '../cachingpolicy/CachingPolicy';
import { DeviceFeatures } from '../device';
import { Channel } from '../entities/Channel';
import { Channels } from '../entities/Channels';
import { ChannelsServiceRepositoryImpl, ChannelsServiceRepositoryImplV2 } from '../impls';
import { IChannelServiceRepository, IChannelsService } from '../interfaces';
import { Log } from '../logging';
import { ChannelsServiceV1Contract, ChannelsServiceV2Contract } from '../servicecontracts';
import { Storage } from '../storage';
import { TChannelsV2, TChannelV2 } from '../types';
import { TChannels } from '../types/TChannels';
import { ChannelsType } from '../types/TChannelsType';
import { TServicesParams } from '../types/TServicesParams';
import { DateTime, ImageUrlGenerator } from '../utils';
import { CcbSwitch } from '../utils/ccb';
import { CcbSwitchName } from '../utils/ccb/enums';
import { StationPolicyService } from './StationPolicyService';

type ChannelsIndexMapType = { [channelNumber: string]: number };

export class ChannelsService implements IChannelsService {
    private readonly TAG: string = 'ChannelsService';

    private readonly mParams: TServicesParams;

    private v1Channels: IChannelServiceRepository<ChannelsServiceV1Contract>;

    private v2Channels: IChannelServiceRepository<ChannelsServiceV2Contract>;

    private static allChannels: Array<string> = [];

    private static allChannelsV2: Array<string> = [];

    private static allChannelsIndexMap: ChannelsIndexMapType = {};

    private static allChannelsIndexMapV2: ChannelsIndexMapType = {};

    private static favoriteChannels: Array<string> = [];

    private static favoriteChannelsV2: Array<string> = [];

    private static favoriteChannelsIndexMap: ChannelsIndexMapType = {};

    private static favoriteChannelsIndexMapV2: ChannelsIndexMapType = {};

    private static shouldUpdateIndexMapping: boolean = true;

    private static shouldUpdateIndexMappingV2: boolean = true;

    private static subscribedChannels: Array<string> = [];

    private static subscribedChannelsV2: Array<string> = [];

    private static subscribedChannelsIndexMap: ChannelsIndexMapType = {};

    private static subscribedChannelsIndexMapV2: ChannelsIndexMapType = {};

    private static readonly stationPoliciesMap: Map<string, StationPolicy> = new Map();

    private static readonly stationPoliciesMapV2: Map<string, StationPolicy> = new Map();

    private static readonly ENDPOINT = {
        V1: 'v1',
        V2: 'v2',
    };

    public constructor(params: TServicesParams) {
        this.mParams = params;
        this.v1Channels = new ChannelsServiceRepositoryImpl(params);
        this.v2Channels = new ChannelsServiceRepositoryImplV2(params);
        this.updateArrayIndexMapping();
        this.updateArrayIndexMappingV2();
    }

    // eslint-disable-next-line class-methods-use-this
    private clearV1StaticStorage() {
        ChannelsService.allChannelsIndexMap = {};
        ChannelsService.allChannels = [];
        ChannelsService.subscribedChannels = [];
        ChannelsService.subscribedChannelsIndexMap = {};
        ChannelsService.favoriteChannels = [];
        ChannelsService.favoriteChannelsIndexMap = {};
        ChannelsService.stationPoliciesMap.clear();
    }

    // eslint-disable-next-line class-methods-use-this
    private clearV2StaticStorage() {
        ChannelsService.allChannelsIndexMapV2 = {};
        ChannelsService.allChannelsV2 = [];
        ChannelsService.subscribedChannelsV2 = [];
        ChannelsService.subscribedChannelsIndexMapV2 = {};
        ChannelsService.favoriteChannelsV2 = [];
        ChannelsService.favoriteChannelsIndexMapV2 = {};
        ChannelsService.stationPoliciesMapV2.clear();
    }

    // eslint-disable-next-line class-methods-use-this
    private isChannelEntitled(channelInfo: {
        sourceType?: string;
        entitlementWindowStart?: string;
        entitlementWindowEnd?: string;
    }): boolean {
        /**
         * reference:
         * https://opengrok.tivo.com/source/xref/clientcore/mainline/shared/models/models/lib/com/tivo/uimodels/model/channel/CloudCoreChannelModelImpl.hx#683
         */
        if (!channelInfo) return false;

        let channelEntitled: boolean = false;
        const nowMs = Date.now();
        if (channelInfo.sourceType === SourceType.JumpChannel) {
            channelEntitled = true;
        } else if (
            channelInfo.entitlementWindowStart &&
            new DateTime(channelInfo.entitlementWindowStart).getTime() > nowMs
        ) {
            // unentitled
            channelEntitled = false;
        } else if (
            channelInfo.entitlementWindowEnd &&
            new DateTime(channelInfo.entitlementWindowEnd).getTime() < nowMs
        ) {
            // unentitled
            channelEntitled = false;
        } else {
            if (channelInfo.entitlementWindowEnd) {
                /**
                 * set the status change timestamp to the end of entitlement window
                 * statusChangeTimestamp =
                 * Math.min(statusChangeTimestamp, channel.entitlementWindowEnd.getTime());
                 * execute timer and update channels information
                 * currently it is not in the target scope
                 * just adding placeholder for future use
                 */
            }
            channelEntitled = true;
        }
        return channelEntitled;
    }

    // eslint-disable-next-line class-methods-use-this
    private isChannelFavorite(channelInfo: { isFavorite?: boolean }): boolean {
        return !!channelInfo.isFavorite;
    }

    private async fetchAndStoreAllChannels(): Promise<void> {
        try {
            const [{ channels: allChannels, filteredOutChannels }, stationPolicyData] =
                await Promise.all([
                    this.getChannelsFromServer(),
                    new StationPolicyService(this.mParams).fetchStationPolicies(),
                ]);

            if (!allChannels.channels.length) {
                allChannels.channels = this.getChannelsFromStore<Channel>(
                    ChannelsService.ENDPOINT.V1,
                );
            }

            const subscribedChannels = {
                channels: allChannels.channels.filter((channel) => this.isChannelEntitled(channel)),
            };
            const favoriteChannels = {
                channels: allChannels.channels.filter((channel) => this.isChannelFavorite(channel)),
            };

            allChannels.channels.forEach((channel, _index) => {
                ChannelsService.allChannelsIndexMap[channel.channelNumber!] = _index;
            });

            if (subscribedChannels.channels.length) {
                subscribedChannels.channels.forEach((channel) => {
                    const channelIndex =
                        ChannelsService.allChannelsIndexMap[channel.channelNumber!];
                    const unsubscribedChannel = allChannels.channels[channelIndex];
                    unsubscribedChannel.isSubscribed = true;
                });
            }

            favoriteChannels.channels.forEach((channel) => {
                const channelIndex = ChannelsService.allChannelsIndexMap[channel.channelNumber!];
                const favoriteChannel = allChannels.channels[channelIndex];
                favoriteChannel.isFavorite = true;
            });

            Storage.getInstance().setItem(Storage.Keys.CHANNELS, allChannels.channels);
            Storage.getInstance().setItem(Storage.Keys.FILTERED_OUT_CHANNELS, filteredOutChannels);
            Storage.getInstance().setItem(
                Storage.Keys.CHANNELS_SNAPSHOT_VERSION,
                allChannels.snapshotVersion,
            );

            if (stationPolicyData) {
                stationPolicyData.forEach((policy: StationPolicy) => {
                    ChannelsService.stationPoliciesMap.set(policy.name, policy);
                });
            }
        } catch (error) {
            Log.e(this.TAG, 'fetchAndStoreAllChannels() - cannot store channels data', error);
            throw error;
        }
    }

    private async fetchAndStoreAllChannelsV2(): Promise<void> {
        try {
            const [allChannels, stationPolicyData] = await Promise.all([
                this.getChannelsFromServerV2(),
                new StationPolicyService(this.mParams).fetchStationPolicies(),
            ]);

            if (!allChannels.channels.length) {
                allChannels.channels = this.getChannelsFromStore<TChannelV2>(
                    ChannelsService.ENDPOINT.V2,
                );
            }

            const subscribedChannels = {
                channels: allChannels.channels.filter((channel) => this.isChannelEntitled(channel)),
            };
            const favoriteChannels = {
                channels: allChannels.channels.filter((channel) => this.isChannelFavorite(channel)),
            };

            allChannels.channels.forEach((channel, _index) => {
                ChannelsService.allChannelsIndexMapV2[channel.channelNumber!] = _index;
            });

            if (subscribedChannels.channels.length) {
                subscribedChannels.channels.forEach((channel) => {
                    const channelIndex =
                        ChannelsService.allChannelsIndexMapV2[channel.channelNumber!];
                    const unsubscribedChannel = allChannels.channels[channelIndex];
                    unsubscribedChannel.isSubscribed = true;
                });
            }

            favoriteChannels.channels.forEach((channel) => {
                const channelIndex = ChannelsService.allChannelsIndexMapV2[channel.channelNumber!];
                const favoriteChannel = allChannels.channels[channelIndex];
                favoriteChannel.isFavorite = true;
            });

            Storage.getInstance().setItem(Storage.Keys.CHANNELS_V2, allChannels.channels);
            Storage.getInstance().setItem(
                Storage.Keys.FILTERED_OUT_CHANNELS_V2,
                allChannels.filteredOutChannels,
            );

            if (stationPolicyData) {
                stationPolicyData.forEach((policy: StationPolicy) => {
                    ChannelsService.stationPoliciesMapV2.set(policy.name, policy);
                });
            }
        } catch (error) {
            Log.e(this.TAG, 'fetchAndStoreAllChannelsV2() - cannot store channels data', error);
            throw error;
        }
    }

    private async getChannelsFromServer(
        isFavorite: boolean = false,
        isReceived: boolean = false,
    ): Promise<{ channels: Channels; filteredOutChannels: Channel[] }> {
        const responseData = await this.v1Channels.getChannelsFromServer(isFavorite, isReceived);

        const allChannels =
            responseData.channels?.map(
                (channel) => new Channel(channel as IChannel, this.mParams.channelLogoBaseUrl),
            ) || [];

        const filteredChannels = this.getFilteredChannels(allChannels);
        const filteredOutChannels = allChannels.filter(
            (channel) => !filteredChannels.includes(channel),
        );

        const channels = new Channels({
            ...responseData,
            channels: filteredChannels,
        });

        return { channels, filteredOutChannels };
    }

    private async getChannelsFromServerV2(
        isFavorite: boolean = false,
        isReceived: boolean = false,
    ): Promise<{ filteredOutChannels: Array<TChannelV2> } & TChannelsV2> {
        const responseData = await this.v2Channels.getChannelsFromServer(isFavorite, isReceived);
        const oImageUrlGenerator = new ImageUrlGenerator();
        const channels =
            responseData.channels?.map((channel): TChannelV2 => {
                const channelLogoUrl =
                    channel.sourceType === SourceType.JumpChannel
                        ? oImageUrlGenerator.buildImageUrlForJumpChannelLogo(
                              this.mParams.channelLogoBaseUrl,
                              channel.jumpChannelInfo?.appLaunchInfo.applicationId!,
                          )
                        : oImageUrlGenerator.buildImageUrlForChannelLogo(
                              this.mParams.channelLogoBaseUrl,
                              channel.logoIndex!,
                          );
                const affiliate =
                    channel.sourceType === SourceType.JumpChannel
                        ? channel.jumpChannelInfo?.metadata.name
                        : channel.affiliate;
                return {
                    ...channel,
                    channelLogoUrl,
                    affiliate,
                    isSubscribed: false,
                };
            }) || [];
        const filteredChannelsResponse = this.getFilteredChannels<TChannelV2>(channels);
        const filteredOutChannels = channels.filter(
            (channel) => !filteredChannelsResponse.includes(channel),
        );
        return {
            ...responseData,
            channels: filteredChannelsResponse,
            channelCount: filteredChannelsResponse.length,
            filteredOutChannels,
            currentOffset: 0,
        };
    }

    private static getChannelsIndexMap(channelsType: string): ChannelsIndexMapType {
        return channelsType === ChannelsType.FAVORITE
            ? this.favoriteChannelsIndexMap
            : this.allChannelsIndexMap;
    }

    private static getChannelsIndexMapV2(channelsType: string): ChannelsIndexMapType {
        return channelsType === ChannelsType.FAVORITE
            ? ChannelsService.favoriteChannelsIndexMapV2
            : ChannelsService.allChannelsIndexMapV2;
    }

    private getChannelsList(channelsType: string): Channel[] {
        const allChannels = this.getChannelsFromStore<Channel>(ChannelsService.ENDPOINT.V1);

        return allChannels.filter((channel) =>
            channelsType === ChannelsType.FAVORITE ? channel.isFavorite : channel,
        );
    }

    private getChannelsListV2(channelsType: string): TChannelV2[] {
        const allChannels = this.getChannelsFromStore<TChannelV2>(ChannelsService.ENDPOINT.V2);

        return allChannels.filter((channel) =>
            channelsType === ChannelsType.FAVORITE ? channel.isFavorite : channel,
        );
    }

    private getChannelsFromStore<T>(version: string): Array<T> {
        Log.i(
            this.TAG,
            'getChannelsFromStore',
            'fetching channels from updated store for version:',
            version,
        );
        if (version === ChannelsService.ENDPOINT.V2)
            return Storage.getInstance().getItem(Storage.Keys.CHANNELS_V2);
        return Storage.getInstance().getItem(Storage.Keys.CHANNELS);
    }

    private getFilteredChannels<
        T extends {
            sourceType?: string;
            isPayPerView?: boolean;
            videoResolution?: string;
        },
    >(channels: T[]): T[] {
        Log.i(this.TAG, 'filtering channels based on various flags');
        let filteredChannels = channels;
        // TODO: Remove this check after completion of ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189
        const isJumpChannelCcbEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189,
        );
        filteredChannels =
            !isJumpChannelCcbEnabled && DeviceFeatures.shouldFilterJumpChannels()
                ? filteredChannels?.filter(
                      (channel) => channel.sourceType !== SourceType.JumpChannel,
                  )
                : filteredChannels;

        filteredChannels = DeviceFeatures.shouldFilterPpvChannels()
            ? filteredChannels?.filter((channel) => !channel.isPayPerView)
            : filteredChannels;

        filteredChannels = DeviceFeatures.shouldFilter4KChannels()
            ? filteredChannels?.filter((channel) => channel.videoResolution !== VideoResolution.Uhd)
            : filteredChannels;
        return filteredChannels;
    }

    // eslint-disable-next-line class-methods-use-this
    private getChannelsAndChannelsIndexMap(channelsType: string) {
        let channelsIndexMap;
        let channels;
        switch (channelsType) {
            case ChannelsType.ALL:
                channelsIndexMap = ChannelsService.allChannelsIndexMap;
                channels = ChannelsService.allChannels;

                break;
            case ChannelsType.FAVORITE: // favourites
                channelsIndexMap = ChannelsService.favoriteChannelsIndexMap;
                channels = ChannelsService.favoriteChannels;

                break;
            case ChannelsType.SUBSCRIBED: // subscribed
            default:
                channels = ChannelsService.subscribedChannels;
                channelsIndexMap = ChannelsService.subscribedChannelsIndexMap;
                break;
        }
        return { channels, channelsIndexMap };
    }

    // eslint-disable-next-line class-methods-use-this
    private getChannelsAndChannelsIndexMapV2(channelsType: string) {
        let channelsIndexMap;
        let channels;
        switch (channelsType) {
            case ChannelsType.ALL:
                channelsIndexMap = ChannelsService.allChannelsIndexMapV2;
                channels = ChannelsService.allChannelsV2;

                break;
            case ChannelsType.FAVORITE: // favourites
                channelsIndexMap = ChannelsService.favoriteChannelsIndexMapV2;
                channels = ChannelsService.favoriteChannelsV2;

                break;
            case ChannelsType.SUBSCRIBED: // subscribed
            default:
                channels = ChannelsService.subscribedChannelsV2;
                channelsIndexMap = ChannelsService.subscribedChannelsIndexMapV2;
                break;
        }
        return { channels, channelsIndexMap };
    }

    private async getChannels(
        type: ChannelsType,
        offset?: number,
        limit?: number,
    ): Promise<TChannels> {
        try {
            if (limit && limit < 0) throw new Error('invalid value of limit');
            if (offset && offset < 0) throw new Error('invalid value of offset');

            await this.updateChannelsCache();
            const allChannels = this.getChannelsFromStore<Channel>(ChannelsService.ENDPOINT.V1);
            const channels: Array<string> = ChannelsService[type];
            const channelCount = channels?.length;
            const start = !offset ? 0 : offset;
            let resultChannelsCount = channelCount;
            if (limit) {
                resultChannelsCount = Math.min(limit, channelCount);
            }
            const end = start + resultChannelsCount;
            let requiredChannels = channels?.slice(start, end);
            /**
             * logic to add channels from starting if the available channels from offset does not
             * fullfil the limit. it will append the remaining channels from the starting,
             * producing cyclic view in guide
             */
            if (end > channelCount) {
                requiredChannels = requiredChannels.concat(channels?.slice(0, end % channelCount));
            }

            const requiredChannelsData = requiredChannels?.map((channelNumber) => {
                const channelIndex = ChannelsService.allChannelsIndexMap[channelNumber];
                const requiredChannel = allChannels[channelIndex];
                requiredChannel.policyInfo = requiredChannel.policyName?.map((policy) => {
                    return ChannelsService.stationPoliciesMap.get(policy);
                });
                return requiredChannel;
            });

            return <TChannels>{
                channels: requiredChannelsData,
                channelCount,
                currentOffset: offset,
            };
        } catch (error) {
            Log.e(this.TAG, 'getChannels()', error);
            throw error;
        }
    }

    private async getChannelsV2(
        type: ChannelsType,
        offset?: number,
        limit?: number,
    ): Promise<TChannelsV2> {
        try {
            if (limit && limit < 0) throw new Error('invalid value of limit');
            if (offset && offset < 0) throw new Error('invalid value of offset');

            await this.updateChannelsCacheV2();
            const allChannels = this.getChannelsFromStore<TChannelV2>(ChannelsService.ENDPOINT.V2);
            let channels = ChannelsService.subscribedChannelsV2;
            if (type === ChannelsType.FAVORITE) {
                channels = ChannelsService.favoriteChannelsV2;
            } else if (type === ChannelsType.ALL) {
                channels = ChannelsService.allChannelsV2;
            }
            const channelCount = channels?.length;
            const start = !offset ? 0 : offset;
            let resultChannelsCount = channelCount;
            if (limit) {
                resultChannelsCount = Math.min(limit, channelCount);
            }
            const end = start + resultChannelsCount;
            let requiredChannels = channels?.slice(start, end);
            /**
             * logic to add channels from starting if the available channels from offset does not
             * fullfil the limit. it will append the remaining channels from the starting,
             * producing cyclic view in guide
             */
            if (end > channelCount) {
                requiredChannels = requiredChannels.concat(channels?.slice(0, end % channelCount));
            }

            const requiredChannelsData = requiredChannels?.map((channelNumber) => {
                const channelIndex = ChannelsService.allChannelsIndexMapV2[channelNumber];
                const requiredChannel = allChannels[channelIndex];
                requiredChannel.policyInfo = requiredChannel.policyName
                    ?.filter((policy) => ChannelsService.stationPoliciesMapV2.get(policy))
                    .map((policy) => {
                        return ChannelsService.stationPoliciesMapV2.get(policy)!;
                    });
                return requiredChannel;
            });

            return <TChannelsV2>{
                channels: requiredChannelsData,
                channelCount,
                currentOffset: offset,
            };
        } catch (error) {
            Log.e(this.TAG, 'getChannelsV2()', error);
            throw error;
        }
    }

    private updateArrayIndexMapping(): void {
        if (!ChannelsService.shouldUpdateIndexMapping) return;

        this.clearV1StaticStorage();
        let channels = this.getChannelsFromStore<Channel>(ChannelsService.ENDPOINT.V1);
        channels = this.getFilteredChannels<Channel>(channels);
        Storage.getInstance().setItem(Storage.Keys.CHANNELS, channels);

        let subscribedChannelsIndex = 0;
        let favoriteChannelsIndex = 0;

        if (channels) {
            channels.forEach((channel, _index) => {
                ChannelsService.allChannelsIndexMap[channel.channelNumber!] = _index;
                ChannelsService.allChannels.push(channel.channelNumber!);

                if (channel.isSubscribed) {
                    ChannelsService.subscribedChannels.push(channel.channelNumber!);
                    ChannelsService.subscribedChannelsIndexMap[channel.channelNumber!] =
                        subscribedChannelsIndex;
                    subscribedChannelsIndex += 1;
                }

                if (channel.isFavorite) {
                    ChannelsService.favoriteChannels.push(channel.channelNumber!);
                    ChannelsService.favoriteChannelsIndexMap[channel.channelNumber!] =
                        favoriteChannelsIndex;
                    favoriteChannelsIndex += 1;
                }
            });
        }

        const stationPolicyData = Storage.getInstance().getItem(Storage.Keys.STATION_POLICIES);
        if (stationPolicyData) {
            stationPolicyData.forEach((policy: StationPolicy) => {
                ChannelsService.stationPoliciesMap.set(policy.name, policy);
            });
        }
        ChannelsService.shouldUpdateIndexMapping = false;
    }

    private updateArrayIndexMappingV2(): void {
        if (!ChannelsService.shouldUpdateIndexMappingV2) return;

        this.clearV2StaticStorage();
        let channels = this.getChannelsFromStore<TChannelV2>(ChannelsService.ENDPOINT.V2);
        channels = this.getFilteredChannels(channels);
        Storage.getInstance().setItem(Storage.Keys.CHANNELS_V2, channels);

        let subscribedChannelsIndex = 0;
        let favoriteChannelsIndex = 0;

        if (channels) {
            channels.forEach((channel, _index) => {
                ChannelsService.allChannelsIndexMapV2[channel.channelNumber!] = _index;
                ChannelsService.allChannelsV2.push(channel.channelNumber!);

                if (channel.isSubscribed) {
                    ChannelsService.subscribedChannelsV2.push(channel.channelNumber!);
                    ChannelsService.subscribedChannelsIndexMapV2[channel.channelNumber!] =
                        subscribedChannelsIndex;
                    subscribedChannelsIndex += 1;
                }

                if (channel.isFavorite) {
                    ChannelsService.favoriteChannelsV2.push(channel.channelNumber!);
                    ChannelsService.favoriteChannelsIndexMapV2[channel.channelNumber!] =
                        favoriteChannelsIndex;
                    favoriteChannelsIndex += 1;
                }
            });
        }

        const stationPolicyData = Storage.getInstance().getItem(Storage.Keys.STATION_POLICIES);
        if (stationPolicyData) {
            stationPolicyData.forEach((policy: StationPolicy) => {
                ChannelsService.stationPoliciesMapV2.set(policy.name, policy);
            });
        }
        ChannelsService.shouldUpdateIndexMappingV2 = false;
    }

    private async updateChannelsCache(): Promise<void> {
        try {
            const currentTime = Date.now();
            const channelsDataExpiryTime = Storage.getInstance().getItem(
                Storage.Keys.CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME,
            );
            const localCacheExpired: boolean =
                !channelsDataExpiryTime || channelsDataExpiryTime < currentTime;
            if (localCacheExpired) {
                await this.fetchAndStoreAllChannels();
                ChannelsService.shouldUpdateIndexMapping = true;
                this.updateArrayIndexMapping();
                Storage.getInstance().setItem(
                    Storage.Keys.CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME,
                    currentTime + CachingPolicy.ChannelsService.cachingDuration,
                );
            }
        } catch (error) {
            Log.e(this.TAG, 'updateChannelsCache()', error);
            if (!Storage.getInstance().getItem(Storage.Keys.CHANNELS)) throw error;
        }
    }

    private async updateChannelsCacheV2(): Promise<void> {
        try {
            const currentTime = Date.now();
            const channelsDataExpiryTime = Storage.getInstance().getItem(
                Storage.Keys.CHANNELS_V2_DATA_LOCAL_CACHE_EXPIRY_TIME,
            );
            const localCacheExpired: boolean =
                !channelsDataExpiryTime || channelsDataExpiryTime < currentTime;
            if (localCacheExpired) {
                await this.fetchAndStoreAllChannelsV2();
                ChannelsService.shouldUpdateIndexMappingV2 = true;
                this.updateArrayIndexMappingV2();
                Storage.getInstance().setItem(
                    Storage.Keys.CHANNELS_V2_DATA_LOCAL_CACHE_EXPIRY_TIME,
                    currentTime + CachingPolicy.ChannelsService.cachingDuration,
                );
            }
        } catch (error) {
            Log.e(this.TAG, 'updateChannelsCacheV2()', error);
            if (!Storage.getInstance().getItem(Storage.Keys.CHANNELS)) throw error;
        }
    }

    /**
     * Returns array of channels object for the given range,
     * if limit and offset is not passed, array of all channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannels>} object of {channels,channelCount}
     */
    public async fetchAllChannels(offset?: number, limit?: number): Promise<TChannels> {
        return this.getChannels(ChannelsType.ALL, offset, limit);
    }

    /**
     * Returns array of channels object for the given range,
     * if limit and offset is not passed, array of all channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannelsV2>} object of {channels,channelCount}
     */
    public async fetchAllChannelsV2(offset?: number, limit?: number): Promise<TChannelsV2> {
        return this.getChannelsV2(ChannelsType.ALL, offset, limit);
    }

    /**
     * Returns array of favorite channels object for the given range,
     * if limit and offset is not passed, array of favorite channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannels>} object of {channels,channelCount}
     */
    public async fetchFavoriteChannels(offset?: number, limit?: number): Promise<TChannels> {
        return this.getChannels(ChannelsType.FAVORITE, offset, limit);
    }

    /**
     * Returns array of favorite channels object for the given range,
     * if limit and offset is not passed, array of favorite channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannelsV2>} object of {channels,channelCount}
     */
    public async fetchFavoriteChannelsV2(offset?: number, limit?: number): Promise<TChannelsV2> {
        return this.getChannelsV2(ChannelsType.FAVORITE, offset, limit);
    }

    /**
     * Returns array of subscribed channels object for the given range,
     * if limit and offset is not passed, array of all subscribed channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannels>} object of {channels,channelCount}
     */
    public async fetchMyChannels(offset?: number, limit?: number): Promise<TChannels> {
        return this.getChannels(ChannelsType.SUBSCRIBED, offset, limit);
    }

    /**
     * Returns array of subscribed channels object for the given range,
     * if limit and offset is not passed, array of all subscribed channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannelsV2>} object of {channels,channelCount}
     */
    public async fetchMyChannelsV2(offset?: number, limit?: number): Promise<TChannelsV2> {
        return this.getChannelsV2(ChannelsType.SUBSCRIBED, offset, limit);
    }

    /**
     * Returns array of channels object from the supplied channelNumber as
     * accordance with the channelsType and limit.
     * @param {string} channelNumber
     * @param {ChannelsType} channelsType
     * @param {number} limit
     * @return {*}  {Promise<TChannels>}
     * @memberof ChannelsService
     */
    public async fetchNextChannelsFromChannelNumber(
        channelNumber: string,
        channelsType: ChannelsType,
        limit: number,
    ): Promise<TChannels> {
        try {
            await this.updateChannelsCache();

            let channelsIndexMap;
            switch (channelsType) {
                case ChannelsType.ALL:
                    channelsIndexMap = ChannelsService.allChannelsIndexMap;
                    break;
                case ChannelsType.FAVORITE:
                    channelsIndexMap = ChannelsService.favoriteChannelsIndexMap;
                    break;
                case ChannelsType.SUBSCRIBED:
                default:
                    channelsIndexMap = ChannelsService.subscribedChannelsIndexMap;
                    break;
            }

            const currentChannelIndex = channelsIndexMap[channelNumber];
            if (currentChannelIndex)
                return this.getChannels(channelsType, currentChannelIndex, limit);
            const channelNumbers = Object.keys(channelsIndexMap);
            const nextChannelNumberIndex = channelNumbers.findIndex(
                (number) => Number(number) > Number(channelNumber),
            );
            if (nextChannelNumberIndex <= 0)
                return this.getChannels(channelsType, channelNumbers.length - 1, limit);
            return this.getChannels(channelsType, nextChannelNumberIndex - 1, limit);
        } catch (error) {
            Log.e(this.TAG, 'fetchNextChannesl', error);
            throw error;
        }
    }

    /**
     * Returns array of channels object from the supplied channelNumber as
     * accordance with the channelsType and limit.
     * @param {string} channelNumber
     * @param {ChannelsType} channelsType
     * @param {number} limit
     * @return {*}  {Promise<TChannelsV2>}
     * @memberof ChannelsService
     */
    public async fetchNextChannelsFromChannelNumberV2(
        channelNumber: string,
        channelsType: ChannelsType,
        limit: number,
    ): Promise<TChannelsV2> {
        try {
            await this.updateChannelsCacheV2();

            let channelsIndexMap;
            switch (channelsType) {
                case ChannelsType.ALL:
                    channelsIndexMap = ChannelsService.allChannelsIndexMapV2;
                    break;
                case ChannelsType.FAVORITE:
                    channelsIndexMap = ChannelsService.favoriteChannelsIndexMapV2;
                    break;
                case ChannelsType.SUBSCRIBED:
                default:
                    channelsIndexMap = ChannelsService.subscribedChannelsIndexMapV2;
                    break;
            }

            const currentChannelIndex = channelsIndexMap[channelNumber];
            if (currentChannelIndex)
                return this.getChannelsV2(channelsType, currentChannelIndex, limit);
            const channelNumbers = Object.keys(channelsIndexMap);
            const nextChannelNumberIndex = channelNumbers.findIndex(
                (number) => Number(number) > Number(channelNumber),
            );
            if (nextChannelNumberIndex <= 0)
                return this.getChannelsV2(channelsType, channelNumbers.length - 1, limit);
            return this.getChannelsV2(channelsType, nextChannelNumberIndex - 1, limit);
        } catch (error) {
            Log.e(this.TAG, 'fetchNextChanneslV2', error);
            throw error;
        }
    }

    /**
     * return next channel object from the currnt channel number
     * @param channelNumber {string} channel number
     * @returns {Channel} channel object of next channel
     */
    public async getNextChannel(
        channelNumber: string,
        channelsType: string,
        count: number = 1,
    ): Promise<Channel> {
        try {
            await this.updateChannelsCache();
            const { channels, channelsIndexMap } =
                this.getChannelsAndChannelsIndexMap(channelsType);
            const currentChannelIndex = channelsIndexMap[channelNumber] || 0;
            const allChannels = this.getChannelsList(channelsType);
            const nextChannelIndex =
                (currentChannelIndex + (count % channels.length)) % channels.length;
            const nextChannelNumber = channels[nextChannelIndex];
            const nextChannelIndexInAllChannels =
                ChannelsService.getChannelsIndexMap(channelsType)[nextChannelNumber];
            const nextRequiredChannel = allChannels[nextChannelIndexInAllChannels];
            nextRequiredChannel.policyInfo = nextRequiredChannel.policyName?.map((policy) =>
                ChannelsService.stationPoliciesMap.get(policy),
            );

            return nextRequiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'getNextChannel', error);
            throw error;
        }
    }

    /**
     * return next channel object from the currnt channel number
     * @param channelNumber {string} channel number
     * @returns {TChannelV2} channel object of next channel
     */
    public async getNextChannelV2(
        channelNumber: string,
        channelsType: string,
        count: number = 1,
    ): Promise<TChannelV2> {
        try {
            await this.updateChannelsCacheV2();
            const { channels, channelsIndexMap } =
                this.getChannelsAndChannelsIndexMapV2(channelsType);
            const currentChannelIndex = channelsIndexMap[channelNumber] || 0;
            const allChannels = this.getChannelsListV2(channelsType);
            const nextChannelIndex =
                (currentChannelIndex + (count % channels.length)) % channels.length;
            const nextChannelNumber = channels[nextChannelIndex];
            const nextChannelIndexInAllChannels =
                ChannelsService.getChannelsIndexMapV2(channelsType)[nextChannelNumber];
            const nextRequiredChannel = allChannels[nextChannelIndexInAllChannels];
            nextRequiredChannel.policyInfo = nextRequiredChannel.policyName
                ?.filter((policy) => ChannelsService.stationPoliciesMapV2.get(policy))
                .map((policy) => {
                    return ChannelsService.stationPoliciesMapV2.get(policy)!;
                });

            return nextRequiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'getNextChannelV2', error);
            throw error;
        }
    }

    /**
     * return previous channel object from the currnt channel number
     * @param channelNumber {string} channel number
     * @returns {Channel} channel object of previous channel
     */
    public async getPreviousChannel(
        channelNumber: string,
        channelsType: string,
        count: number = 1,
    ): Promise<Channel> {
        try {
            const { channels, channelsIndexMap } =
                this.getChannelsAndChannelsIndexMap(channelsType);
            await this.updateChannelsCache();
            const allChannels = this.getChannelsList(channelsType);
            const currentChannelIndex = channelsIndexMap[channelNumber];
            const previousChannelIndex =
                (currentChannelIndex - (count % channels.length) + channels.length) %
                channels.length;

            const previousChannelNumber = channels[previousChannelIndex];
            const previousChannelIndexInAllChannels =
                ChannelsService.getChannelsIndexMap(channelsType)[previousChannelNumber];
            const previousRequiredChannel = allChannels[previousChannelIndexInAllChannels];
            previousRequiredChannel.policyInfo = previousRequiredChannel.policyName?.map(
                (policy) => {
                    return ChannelsService.stationPoliciesMap.get(policy);
                },
            );
            return previousRequiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'getPreviousChannel', error);
            throw error;
        }
    }

    /**
     * return previous channel object from the currnt channel number
     * @param channelNumber {string} channel number
     * @returns {TChannelV2} channel object of previous channel
     */
    public async getPreviousChannelV2(
        channelNumber: string,
        channelsType: string,
        count: number = 1,
    ): Promise<TChannelV2> {
        try {
            const { channels, channelsIndexMap } =
                this.getChannelsAndChannelsIndexMapV2(channelsType);
            await this.updateChannelsCacheV2();
            const allChannels = this.getChannelsListV2(channelsType);
            const currentChannelIndex = channelsIndexMap[channelNumber];
            const previousChannelIndex =
                (currentChannelIndex - (count % channels.length) + channels.length) %
                channels.length;

            const previousChannelNumber = channels[previousChannelIndex];
            const previousChannelIndexInAllChannels =
                ChannelsService.getChannelsIndexMapV2(channelsType)[previousChannelNumber];
            const previousRequiredChannel = allChannels[previousChannelIndexInAllChannels];
            previousRequiredChannel.policyInfo = previousRequiredChannel.policyName
                ?.filter((policy) => ChannelsService.stationPoliciesMapV2.get(policy))
                .map((policy) => {
                    return ChannelsService.stationPoliciesMapV2.get(policy)!;
                });
            return previousRequiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'getPreviousChannel', error);
            throw error;
        }
    }

    /**
     * return obect of requested channel bumber
     * @param channelNumber {string} requested channel number
     * @returns {Channel} channel object of requested channel
     */
    public async tuneToChannel(channelNumber: string): Promise<Channel> {
        try {
            const currentChannelIndex = ChannelsService.allChannelsIndexMap[channelNumber];
            if (currentChannelIndex === undefined)
                throw new Error(`cannot find channel with channel number: ${channelNumber}`);
            const channels = this.getChannelsFromStore<Channel>(ChannelsService.ENDPOINT.V1);
            const requiredChannel = channels[currentChannelIndex];
            requiredChannel.policyInfo = requiredChannel.policyName?.map((policy) => {
                return ChannelsService.stationPoliciesMap.get(policy);
            });
            return requiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'tuneToChannel', error);
            throw error;
        }
    }

    /**
     * return obect of requested channel bumber
     * @param channelNumber {string} requested channel number
     * @returns {TChannelV2} channel object of requested channel
     */
    public async tuneToChannelV2(channelNumber: string): Promise<TChannelV2> {
        try {
            const currentChannelIndex = ChannelsService.allChannelsIndexMapV2[channelNumber];
            if (currentChannelIndex === undefined)
                throw new Error(`cannot find channel with channel number: ${channelNumber}`);
            const channels = this.getChannelsFromStore<TChannelV2>(ChannelsService.ENDPOINT.V2);
            const requiredChannel = channels[currentChannelIndex];
            requiredChannel.policyInfo = requiredChannel.policyName
                ?.filter((policy) => ChannelsService.stationPoliciesMapV2.get(policy))
                .map((policy) => {
                    return ChannelsService.stationPoliciesMapV2.get(policy)!;
                });
            return requiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'tuneToChannelV2', error);
            throw error;
        }
    }

    /**
     * Update and stores the channels object by adding the tuning URI.
     * @param {string} channelNumber
     * @param {string} url
     * @memberof ChannelsService
     */
    public updateChannelUrl(channelNumber: string, url: string): void {
        const currentChannelIndex = ChannelsService.allChannelsIndexMap[channelNumber];
        const allChannels = this.getChannelsFromStore<Channel>(ChannelsService.ENDPOINT.V1);
        allChannels[currentChannelIndex].tuningUri = [url];
        Storage.getInstance().setItem(Storage.Keys.CHANNELS, allChannels);
    }

    /**
     * Update and stores the channels object by adding the tuning URI.
     * @param {string} channelNumber
     * @param {string} url
     * @memberof ChannelsService
     */
    public updateChannelUrlV2(channelNumber: string, url: string): void {
        const currentChannelIndex = ChannelsService.allChannelsIndexMapV2[channelNumber];
        const allChannels = this.getChannelsFromStore<TChannelV2>(ChannelsService.ENDPOINT.V2);
        allChannels[currentChannelIndex].tuningUri = [url];
        Storage.getInstance().setItem(Storage.Keys.CHANNELS_V2, allChannels);
    }

    /**
     * This method will be used to update the a channel to favorite or not in localStorage
     * We try to do optimistic updates.
     * @param channel
     * @param isFavorite
     */
    public async updateFavoriteChannel(channel: Channel, isFavorite: boolean): Promise<void> {
        const channels = this.getChannelsFromStore<Channel>(ChannelsService.ENDPOINT.V1);
        channels.forEach((ch, index) => {
            if (ch.channelId === channel.channelId) {
                channels[index].isFavorite = isFavorite;
            }
        });
        Storage.getInstance().setItem(Storage.Keys.CHANNELS, channels);
        ChannelsService.shouldUpdateIndexMapping = true;
        this.updateArrayIndexMapping();
    }

    /**
     * This method will be used to update the a channelV2 to favorite or not in localStorage
     * We try to do optimistic updates.
     * @param channel
     * @param isFavorite
     */
    public async updateFavoriteChannelV2(channel: TChannelV2, isFavorite: boolean): Promise<void> {
        const channels = this.getChannelsFromStore<TChannelV2>(ChannelsService.ENDPOINT.V2);
        channels.forEach((ch, index) => {
            if (ch.channelId === channel.channelId) {
                channels[index].isFavorite = isFavorite;
            }
        });
        Storage.getInstance().setItem(Storage.Keys.CHANNELS_V2, channels);
        ChannelsService.shouldUpdateIndexMappingV2 = true;
        this.updateArrayIndexMappingV2();
    }

    /**
     * Fetches the filtered-out (PPV, Jump, 4K) channels from the local store.
     *
     * @returns {Promise<Array<Channel>>} A promise that resolves to an array of
     * filtered-out Channel objects.
     */
    // eslint-disable-next-line class-methods-use-this
    public async fetchFilteredOutChannels(): Promise<Array<Channel>> {
        return Storage.getInstance().getItem(Storage.Keys.FILTERED_OUT_CHANNELS);
    }

    /**
     * Fetches the filtered-out (PPV, Jump, 4K) channels from the local store.
     *
     * @returns {Promise<Array<TChannelV2>>} A promise that resolves to an array of
     * filtered-out TChannelV2 objects.
     */
    // eslint-disable-next-line class-methods-use-this
    public async fetchFilteredOutChannelsV2(): Promise<Array<TChannelV2>> {
        return Storage.getInstance().getItem(Storage.Keys.FILTERED_OUT_CHANNELS_V2);
    }
}
